/**
 * useLeadCaptureSocket — listener GLOBAL de `LEAD_CAPTURE_SESSION_UPDATED`
 * (room `user:{id}`, emitido só por `LeadCaptureService.emit`). Montado uma
 * única vez em `GlobalSocketListeners` (NUNCA #38): vale na modal do CRM, no
 * dashboard do vendedor e em qualquer rota autenticada.
 *
 * Efeito: escreve a sessão no cache `['lead-capture-session', uuid]` (a modal
 * avança sem esperar o polling de 5s) e invalida `['seller-dashboard']`.
 */
import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS, type LeadCaptureSessionResponse, type LeadCaptureSessionUpdatedPayload } from '@troia-v3/types';
import { useEffect } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';

export const useLeadCaptureSocket = (): void => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { state: authState } = useAuth();

  useEffect(() => {
    if (!socket || !authState.user) return;

    const handler = (payload: LeadCaptureSessionUpdatedPayload): void => {
      const { session } = payload;
      queryClient.setQueryData<LeadCaptureSessionResponse>(['lead-capture-session', session.uuid], (prev) =>
        prev ? { ...prev, session } : { session, qrDataUrl: '', url: '' });
      queryClient.invalidateQueries({ queryKey: ['seller-dashboard'] });
    };

    socket.on(SOCKET_EVENTS.LEAD_CAPTURE_SESSION_UPDATED, handler);
    return () => {
      socket.off(SOCKET_EVENTS.LEAD_CAPTURE_SESSION_UPDATED, handler);
    };
  }, [socket, authState.user, queryClient]);
};
